if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        // Replace thumbnail strip arrows so only gallery navigation runs (no thumbnail strip scroll)
        ['previous', 'next'].forEach((name) => {
          const btn = this.elements.thumbnails.querySelector(`button[name="${name}"]`);
          if (!btn) return;
          const clone = btn.cloneNode(true);
          clone.removeAttribute('disabled');
          btn.replaceWith(clone);
          clone.addEventListener('click', (e) => {
            e.preventDefault();
            this.navigateViewer(name === 'next' ? 1 : -1);
          });
        });

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      navigateViewer(direction) {
        // Only consider slides that are actually visible — the Variant Thumbnail Filter
        // (snippets/product-media-gallery.liquid) hides slides not associated with the
        // selected variant via the .vtf-hidden class (display:none !important). Without
        // this filter, prev/next would set is-active on a vtf-hidden slide, and because
        // the desktop thumbnail layout hides every :not(.is-active) media item, the big
        // image would vanish from view.
        const visibleMediaItems = Array.from(
          this.elements.viewer.querySelectorAll('li[data-media-id]:not(.vtf-hidden)')
        );
        if (visibleMediaItems.length < 2) return;

        const activeItem =
          this.elements.viewer.querySelector('li.is-active[data-media-id]:not(.vtf-hidden)') ||
          visibleMediaItems[0];
        const currentIndex = visibleMediaItems.indexOf(activeItem);
        const safeCurrentIndex = currentIndex === -1 ? 0 : currentIndex;
        const nextIndex = Math.max(0, Math.min(visibleMediaItems.length - 1, safeCurrentIndex + direction));
        if (nextIndex !== safeCurrentIndex) {
          this.setActiveMedia(visibleMediaItems[nextIndex].dataset.mediaId, false);
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        // Idempotency guard: variant-change refreshes (prepend=true) sometimes fire
        // multiple times for the SAME featured media id (e.g., on scroll-triggered
        // intersection events, sticky-bar re-syncs, or quiet product-info refreshes).
        // Without this guard, each repeat call would prepend the active slide to slot 0
        // and scrollTo({left:0}), yanking the user back to the first image even though
        // the variant didn't actually change. QA-reported as: "carousel auto-resets to
        // first position on scroll" — PDF Issue 4.
        if (prepend && this._lastFeaturedMediaId === mediaId) {
          return;
        }
        if (prepend) this._lastFeaturedMediaId = mediaId;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        if (!this.mql.matches || this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'instant' });
        }
        // Scroll-to-gallery on variant change intentionally removed — stay at current scroll position.
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
